import { Button, Input, Radio, Spin } from "antd";
import type { TextAreaRef } from "antd/es/input/TextArea";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, ICellRendererParams } from "ag-grid-enterprise";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { message } from "utils/ciq-message";
import {
  cloneDesignScopeLodRows,
  type DesignScopeLodRow,
  type DesignScopeLodScope
} from "./model";
import { useDesignScopeLodSettings } from "./use-design-scope-lod-settings";

/**
 * Draft text stays in this field. Pushing each keystroke into grid row data
 * makes AG Grid destroy the cell, and the caret jumps to the end.
 * autoSize also re-renders the textarea, so the caret is restored after that.
 */
function isBlankLodField(value: string) {
  return value.trim() === "";
}

function draftHasEmptyLodFields(rows: DesignScopeLodRow[]) {
  return rows.some(
    (row) => isBlankLodField(row.name) || isBlankLodField(row.description)
  );
}

function LodDraftTextArea(props: {
  initialValue: string;
  minRows: number;
  maxRows: number;
  testId: string;
  errorMessage: string;
  onValueChange: (value: string) => void;
}) {
  const {
    initialValue,
    minRows,
    maxRows,
    testId,
    errorMessage,
    onValueChange
  } = props;
  const [value, setValue] = useState(initialValue);
  const showError = isBlankLodField(value);
  const areaRef = useRef<TextAreaRef>(null);
  const caretRef = useRef<{ start: number; end: number } | null>(null);

  useLayoutEffect(() => {
    const caret = caretRef.current;
    const element = areaRef.current?.resizableTextArea?.textArea;
    if (!caret || !element || document.activeElement !== element) return;
    if (
      element.selectionStart !== caret.start ||
      element.selectionEnd !== caret.end
    ) {
      element.setSelectionRange(caret.start, caret.end);
    }
  });

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      caretRef.current = null;
    });
    return () => cancelAnimationFrame(frame);
  });

  return (
    <div>
      <Input.TextArea
        ref={areaRef}
        value={value}
        status={showError ? "error" : undefined}
        autoSize={{ minRows, maxRows }}
        className="!rounded-lg !border-ciq-gray-300 text-sm !text-ciq-text-900 hover:!border-ciq-primary-800 focus:!border-ciq-primary-800"
        onChange={(event) => {
          const next = event.target.value;
          caretRef.current = {
            start: event.target.selectionStart,
            end: event.target.selectionEnd
          };
          setValue(next);
          onValueChange(next);
        }}
        data-testid={testId}
      />
      {showError ? (
        <div
          className="mt-1 text-xs"
          style={{ color: "var(--ciq-red-700)" }}
          data-testid={`${testId}-error`}
        >
          {errorMessage}
        </div>
      ) : null}
    </div>
  );
}

function DesignScopeLevelOfDetailSettings(props: {
  disabled: boolean;
  scope: DesignScopeLodScope;
}) {
  const { disabled, scope } = props;
  const gridRef = useRef<AgGridReact<DesignScopeLodRow>>(null);
  const { rows, defaultId, loading, save } = useDesignScopeLodSettings(scope);

  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draftRows, setDraftRows] = useState<DesignScopeLodRow[]>([]);
  const [draftDefaultId, setDraftDefaultId] = useState("");
  const [hasEmptyDraft, setHasEmptyDraft] = useState(false);
  const draftRowsRef = useRef<DesignScopeLodRow[]>([]);

  const replaceDraftRows = useCallback((next: DesignScopeLodRow[]) => {
    draftRowsRef.current = next;
    setDraftRows(next);
  }, []);

  useEffect(() => {
    if (isEditing) return;
    replaceDraftRows(cloneDesignScopeLodRows(rows));
    setDraftDefaultId(defaultId);
  }, [rows, defaultId, isEditing, replaceDraftRows]);

  useLayoutEffect(() => {
    if (!isEditing) return;
    gridRef.current?.api?.resetRowHeights();
  }, [hasEmptyDraft, isEditing]);

  const displayedRows = isEditing ? draftRows : rows;
  const displayedDefaultId = isEditing ? draftDefaultId : defaultId;
  const canEdit = !disabled && !loading && !saving && rows.length > 0;

  const startEdit = () => {
    const next = cloneDesignScopeLodRows(rows);
    replaceDraftRows(next);
    setHasEmptyDraft(draftHasEmptyLodFields(next));
    setDraftDefaultId(defaultId);
    setIsEditing(true);
  };

  const cancelEdit = () => {
    replaceDraftRows(cloneDesignScopeLodRows(rows));
    setHasEmptyDraft(false);
    setDraftDefaultId(defaultId);
    setIsEditing(false);
  };

  const saveEdit = async () => {
    if (draftHasEmptyLodFields(draftRowsRef.current)) return;
    setSaving(true);
    const result = await save(draftRowsRef.current, draftDefaultId);
    setSaving(false);
    if (!result.success) return;
    if (result.message) message.success(result.message);
    setIsEditing(false);
  };

  const updateDraftRow = useCallback(
    (id: string, field: "name" | "description", value: string) => {
      draftRowsRef.current = draftRowsRef.current.map((row) =>
        row.id === id ? { ...row, [field]: value } : row
      );
      setHasEmptyDraft(draftHasEmptyLodFields(draftRowsRef.current));
    },
    []
  );

  const NameCellRenderer = useCallback(
    (params: ICellRendererParams<DesignScopeLodRow>) => {
      const { data } = params;
      if (!data) return null;

      if (isEditing) {
        const initialValue =
          draftRowsRef.current.find((row) => row.id === data.id)?.name ??
          data.name;
        return (
          <LodDraftTextArea
            initialValue={initialValue}
            minRows={2}
            maxRows={4}
            testId={`design-scope-lod-name-${data.id}`}
            errorMessage="Name is required"
            onValueChange={(value) => updateDraftRow(data.id, "name", value)}
          />
        );
      }
      return <span>{data.name}</span>;
    },
    [isEditing, updateDraftRow]
  );

  const DescriptionCellRenderer = useCallback(
    (params: ICellRendererParams<DesignScopeLodRow>) => {
      const { data } = params;
      if (!data) return null;

      if (isEditing) {
        const initialValue =
          draftRowsRef.current.find((row) => row.id === data.id)?.description ??
          data.description;
        return (
          <LodDraftTextArea
            initialValue={initialValue}
            minRows={2}
            maxRows={6}
            testId={`design-scope-lod-description-${data.id}`}
            errorMessage="Description is required"
            onValueChange={(value) =>
              updateDraftRow(data.id, "description", value)
            }
          />
        );
      }
      return (
        <span className="block w-full min-w-0 whitespace-normal break-words leading-5">
          {data.description}
        </span>
      );
    },
    [isEditing, updateDraftRow]
  );

  const DefaultCellRenderer = useCallback(
    (params: ICellRendererParams<DesignScopeLodRow>) => {
      const { data } = params;
      if (!data) return null;

      const isDefault = data.id === displayedDefaultId;

      if (isEditing) {
        return (
          <Radio
            name="design-scope-lod-default"
            checked={isDefault}
            onChange={() => setDraftDefaultId(data.id)}
            data-testid={`design-scope-lod-default-${data.id}`}
          />
        );
      }

      if (isDefault) {
        return (
          <span
            className="inline-flex items-center rounded-md border border-solid border-ciq-primary-950 bg-ciq-primary-bg px-2 py-0.5 text-xs text-ciq-black text-opacity-80"
            data-testid="design-scope-lod-default-badge"
          >
            Default
          </span>
        );
      }

      return null;
    },
    [isEditing, displayedDefaultId]
  );

  const columnDefs = useMemo<ColDef<DesignScopeLodRow>[]>(
    () => [
      {
        headerName: "Level",
        field: "level",
        width: 100,
        minWidth: 80,
        flex: 0,
        sortable: false,
        cellClass: "ciq-cell-v-center"
      },
      {
        headerName: "Name",
        field: "name",
        width: 200,
        minWidth: 150,
        flex: 0,
        sortable: false,
        cellRenderer: NameCellRenderer,
        autoHeight: isEditing,
        cellClass: "ciq-cell-v-center"
      },
      {
        headerName: "Description",
        field: "description",
        flex: 1,
        minWidth: 300,
        sortable: false,
        cellRenderer: DescriptionCellRenderer,
        wrapText: true,
        autoHeight: isEditing,
        cellClass: "ciq-cell-v-center whitespace-normal"
      },
      {
        headerName: isEditing ? "Default" : "",
        field: "id",
        width: 100,
        minWidth: 80,
        flex: 0,
        sortable: false,
        resizable: false,
        cellRenderer: DefaultCellRenderer,
        cellClass: "ciq-cell-v-center justify-center"
      }
    ],
    [isEditing, NameCellRenderer, DescriptionCellRenderer, DefaultCellRenderer]
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: false,
      filter: false,
      resizable: true,
      menuTabs: []
    }),
    []
  );

  const getRowId = useCallback(
    (params: { data: DesignScopeLodRow }) => params.data.id,
    []
  );

  return (
    <div
      className="flex h-full flex-col"
      data-testid="design-scope-lod-settings"
    >
      <div className="flex items-start justify-between px-8 pt-6">
        <div>
          <div className="text-xl font-bold text-ciq-black text-opacity-80">
            Level of Detail
          </div>
          <div className="mt-1 text-sm text-ciq-black text-opacity-60">
            Define deliverable standards for Design Scope items.
          </div>
        </div>
        {isEditing ? (
          <div className="flex space-x-2 text-ciq-white">
            <Button
              type="primary"
              onClick={saveEdit}
              loading={saving}
              disabled={saving || hasEmptyDraft}
              data-testid="save-button"
            >
              Save
            </Button>
            <Button
              onClick={cancelEdit}
              disabled={saving}
              data-testid="cancel-button"
            >
              Cancel
            </Button>
          </div>
        ) : (
          <Button
            type="primary"
            disabled={!canEdit}
            onClick={startEdit}
            data-testid="edit-button"
          >
            Edit
          </Button>
        )}
      </div>

      <div className="flex-1 px-8 pb-8 pt-6">
        <Spin spinning={loading || saving}>
          <div
            className="ag-theme-alpine h-full"
            data-testid="design-scope-lod-grid"
          >
            <AgGridReact<DesignScopeLodRow>
              ref={gridRef}
              rowData={displayedRows}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              getRowId={getRowId}
              headerHeight={40}
              rowHeight={92}
              animateRows={false}
              suppressDragLeaveHidesColumns
              suppressContextMenu
              domLayout="autoHeight"
              suppressCellFocus
              suppressRowHoverHighlight={false}
            />
          </div>
        </Spin>
      </div>
    </div>
  );
}

export default DesignScopeLevelOfDetailSettings;
